// src/hooks/cases/queryKeys.ts
//
// Centralized query-key factories for the case + client-application domains.
// Hierarchical shape so invalidation can be scoped by level:
//   caseKeys.all        → ['cases']                       (invalidates every case query)
//   caseKeys.lists()    → ['cases', 'list']               (every list, any filters)
//   caseKeys.list(f)    → ['cases', 'list', f]            (one filtered list)
//   caseKeys.details()  → ['cases', 'detail']             (every detail + sub-keys)
//   caseKeys.detail(id) → ['cases', 'detail', id]         (one detail; prefix-covers history + kyc-slots)
//   caseKeys.history(id)→ ['cases', 'detail', id, 'history']
//   caseKeys.kycSlots(id)→ ['cases', 'detail', id, 'kyc-slots']
//
// Invalidating caseKeys.detail(id) prefix-matches its history/kyc-slots sub-keys,
// so explicit sub-key invalidations are documentation, not correctness load-bearing.
//
// Parallel clientAppKeys covers the rich ClientApplication + its documents, which
// historically lived under separate top-level prefixes (['client-application', …]
// and ['client-application-documents', …]); they are now nested under one root so
// clientAppKeys.all invalidates both at once when needed.

import type { FilterState } from '@/types'

export const caseKeys = {
  all: ['cases'] as const,
  lists: () => [...caseKeys.all, 'list'] as const,
  list: (filters: FilterState) => [...caseKeys.lists(), filters] as const,
  details: () => [...caseKeys.all, 'detail'] as const,
  // id is `string | undefined` so callers with optional route params can build the
  // key pre-enabled (matches the legacy ['case', applicationId] shape); the query's
  // `enabled: !!id` gate prevents the undefined-id variant from fetching.
  detail: (id: string | undefined) => [...caseKeys.details(), id] as const,
  history: (id: string | undefined) => [...caseKeys.detail(id), 'history'] as const,
  kycSlots: (id: string | undefined) => [...caseKeys.detail(id), 'kyc-slots'] as const,
}

export const clientAppKeys = {
  all: ['client-applications'] as const,
  details: () => [...clientAppKeys.all, 'detail'] as const,
  detail: (proposalId: string | undefined) => [...clientAppKeys.details(), proposalId] as const,
  documents: (applicationId: string | undefined) => [...clientAppKeys.all, 'documents', applicationId] as const,
}

// proposalKeys preserves the pre-unification tuple shapes verbatim — the
// 'proposal' / 'proposals' / 'proposals-stats' prefixes predate the
// caseKeys hierarchy and are still the keys consumers invalidate against
// (e.g. useCreateProposal.onSuccess, useCaseDetailData's SSE callback). The
// factory simply centralizes the literals so a key change is one edit, not
// a grep. Shapes are NOT nested under caseKeys on purpose: doing so would
// silently widen every case invalidation to hit proposal detail caches.
export const proposalKeys = {
  // ['proposal', id] — single proposal detail (+ SSE-driven refetch)
  detail: (id: string | undefined) => ['proposal', id] as const,
  // ['proposals'] — invalidates every proposal list/stats query (create hook)
  lists: () => ['proposals'] as const,
  // ['proposals-stats', includeClosed] — advisor dashboard counts
  stats: (includeClosed: boolean) => ['proposals-stats', includeClosed] as const,
  // ['proposal-catalog'] — GET /api/products for the create form. Not under
  // 'products': useProducts' invalidation of ['products'] must not touch it.
  catalog: () => ['proposal-catalog'] as const,
}
